// ADAPTADOR DE SAIDA DA ALECE — o movimento na estrutura que a folha dela lê.
//
// "O arquivo de movimento que a Atlas enviará para a ALECE deverá ter a mesma
// estrutura do arquivo FOLHA, sem precisar enviar as rubricas tipo B, e sem
// precisar informar o CODFP, e acrescentando mais 2 colunas: PARCELA e PARCTOT"
// — dicionario da ALECE, literal. Decidido em 18/09/2026: PARCELA e PARCTOT
// NAO sao enviadas; sao as nove colunas que a averbadora anterior mandava. Em
// 23/09/2026 elas ficaram FIXAS (sairam as opcoes PAGOFP/MOTIVO e parcela no
// OBS), o separador passou a `;` e o decimal a virgula.
//
// FF e' a folha funcional do servidor e faz parte da chave da linha na ALECE
// (FF + MATR + RUB). Vem de `d.folha`, que o servico preenche a partir do
// retorno (compromisso do contrato ou foto de margem); o cadastro e' o ultimo
// recurso. `||`, e nao `??`: folha vazia nao pode vencer a fonte seguinte.
//
// DOIS MODOS, E A DIFERENCA ENTRE ELES E' O CASAMENTO DO MES SEGUINTE:
//
//   porContrato  uma linha por divida, `1 CONTRATO(S)` no OBS.
//   agregado     uma linha por (FF, matricula, verba), como o arquivo de hoje,
//                com `N CONTRATO(S)` no OBS. O relatorio de geracao lista cada
//                divida que foi somada — a perda fica visivel em vez de silenciosa.
//
// O modo vem da configuracao do orgao (`ctx.opcoesMovimento`), nao do
// chamador: e' propriedade do acordo com o ente.
//
// DESCRUB e' a descricao da rubrica no catalogo da unidade. Sem ela, o nome do
// credor — mas nunca o sentinela do ADR-0010: "Credor a identificar" no lugar
// do nome da rubrica e' informacao falsa para a folha. Nesse caso a celula sai
// vazia e o relatorio acusa `DESCRUB`.
import { CREDOR_A_IDENTIFICAR } from "../../contrato.js";
import { chaveFolha } from "../../margem-informada.js";
import { resumirPorCredor } from "../../movimento-modelo.js";
import { montarLinhaDelimitada } from "../../csv-delimitado.js";
import { layoutDoCatalogo } from "../catalogo.js";
import { ALECE_DECIMAL_MOVIMENTO, ALECE_MOVIMENTO_LARGURA_FF, ALECE_MOVIMENTO_LARGURA_MATR, ALECE_SEPARADOR_MOVIMENTO, COLUNAS_ALECE_MOVIMENTO, } from "./alece-colunas.js";
const CODIGO = "alece-movimento";
function versao() {
    return layoutDoCatalogo(CODIGO).versao;
}
/** Como o arquivo de julho/2026: o cabecalho termina no separador. */
function cabecalho() {
    return COLUNAS_ALECE_MOVIMENTO.map((c) => c.nome).join(ALECE_SEPARADOR_MOVIMENTO) + ALECE_SEPARADOR_MOVIMENTO;
}
/** Zeros a esquerda ate a largura da ALECE; vazio continua vazio (a emissao acusa). */
function comZeros(valor, largura) {
    const v = valor.trim();
    return v === "" ? "" : v.padStart(largura, "0");
}
/** A folha funcional do desconto: a do retorno, senao a do cadastro. */
function folhaDoDesconto(d, ctx) {
    const campos = ctx.camposDoServidor(d.matricula);
    return d.folha || campos.FF || campos.ff || campos.custom_ff || "";
}
/** Descricao da rubrica; sem ela, o credor quando ele e' conhecido. */
function descricaoDoDesconto(d, ctx) {
    const daRubrica = ctx.descricaoDaVerba(d.codigoVerba ?? "");
    if (daRubrica)
        return daRubrica;
    return d.credorId && d.credorId !== CREDOR_A_IDENTIFICAR ? (d.credorNome ?? "") : "";
}
function apenasDigitos(s) {
    return (s ?? "").replace(/\D/g, "");
}
function linha(valores) {
    return montarLinhaDelimitada(COLUNAS_ALECE_MOVIMENTO, valores, ALECE_SEPARADOR_MOVIMENTO, ALECE_DECIMAL_MOVIMENTO);
}
function celulasComuns(d, entrada, ctx) {
    return {
        ANOMES: entrada.competencia,
        FF: comZeros(chaveFolha(folhaDoDesconto(d, ctx)), ALECE_MOVIMENTO_LARGURA_FF),
        MATR: comZeros(d.matricula, ALECE_MOVIMENTO_LARGURA_MATR),
        // Sem mascara: o arquivo da ALECE trabalha com o CPF cru, e a mascara do
        // movimento canonico e' propriedade DAQUELE layout, nao deste.
        CPF: apenasDigitos(d.cpf),
        TIPO: "C",
        RUB: d.codigoVerba ?? "",
        DESCRUB: descricaoDoDesconto(d, ctx),
    };
}
export const adaptadorAleceMovimento = {
    codigo: CODIGO,
    serializar(entrada, ctx) {
        // O layout nao tem coluna de tipo de movimento. Encerramento so' pode ser
        // anunciado por OMISSAO aqui — e fingir que suporta `E` faria a Atlas
        // instruir "pare de descontar" num arquivo que nao sabe dizer isso.
        const comE = entrada.descontos.find((d) => d.tipoMovimento === "E");
        if (comE) {
            throw new Error(`alece-movimento: o layout da ALECE não tem coluna de tipo de movimento e não consegue anunciar ` +
                `encerramento explícito (ADF ${comE.adf}). Use a estratégia de encerramento por omissão nesta unidade.`);
        }
        // Cada campo que ficou vazio, com a chave de quem precisa ser corrigido: a
        // emissao recusa pelos que o layout declara obrigatorios e mostra a lista.
        // DESCRUB acusa a CELULA vazia — o credor conhecido e' descricao aceita.
        const faltantes = new Map();
        const faltou = (campo, chave) => {
            const s = faltantes.get(campo) ?? new Set();
            s.add(chave);
            faltantes.set(campo, s);
        };
        for (const d of entrada.descontos) {
            if (!folhaDoDesconto(d, ctx))
                faltou("FF", d.matricula);
            if (!descricaoDoDesconto(d, ctx))
                faltou("DESCRUB", d.codigoVerba ?? "");
            if (d.origem === "H" && !d.referencia)
                faltou("referencia", d.adf);
        }
        const camposSemCorrespondencia = [...faltantes.keys()];
        const faltantesPorCampo = Object.fromEntries([...faltantes].map(([campo, s]) => [campo, [...s]]));
        const linhas = [cabecalho()];
        const dividasSomadas = [];
        const modo = ctx.opcoesMovimento?.aleceGranularidade === "por_contrato" ? "porContrato" : "agregado";
        if (modo === "porContrato") {
            for (const d of entrada.descontos) {
                linhas.push(linha({
                    ...celulasComuns(d, entrada, ctx),
                    OBS: "1 CONTRATO(S)",
                    VALOR: d.valorParcela,
                }));
            }
        }
        else {
            const grupos = new Map();
            for (const d of entrada.descontos) {
                // FF + MATR + RUB, a chave da linha na folha da ALECE. A folha sai da
                // mesma fonte da celula FF, normalizada so' para comparar.
                const ff = chaveFolha(folhaDoDesconto(d, ctx));
                const chave = `${ff}\u0000${d.matricula}\u0000${d.codigoVerba ?? ""}`;
                const lista = grupos.get(chave) ?? [];
                lista.push(d);
                grupos.set(chave, lista);
            }
            for (const lista of grupos.values()) {
                const primeira = lista[0];
                const valor = lista.reduce((s, d) => s + d.valorParcela, 0);
                // Parcela e total nao vao no arquivo; entram so' no relatorio das
                // dividas somadas. A MENOR parcela e o MAIOR total sao a leitura
                // conservadora de uma linha que junta mais de um contrato.
                const parcela = Math.min(...lista.map((d) => d.parcelaNumero));
                const total = Math.max(...lista.map((d) => d.parcelasTotal));
                linhas.push(linha({
                    ...celulasComuns(primeira, entrada, ctx),
                    OBS: `${lista.length} CONTRATO(S)`,
                    VALOR: valor,
                }));
                if (lista.length > 1) {
                    dividasSomadas.push({
                        matricula: primeira.matricula,
                        codigoVerba: primeira.codigoVerba ?? "",
                        adfs: lista.map((d) => d.adf),
                        valorSomado: Math.round(valor * 100) / 100,
                        parcelaEscrita: parcela,
                        parcelasTotalEscrito: total,
                    });
                }
            }
        }
        const identificadorEscrito = modo === "porContrato" || dividasSomadas.length === 0;
        const avisos = [];
        if (entrada.descontos.length === 0) {
            // FR-023: o `casoVazio: "so-cabecalho"` declarado no catalogo, em
            // palavras. O arquivo com so' o cabecalho e' valido para o importador da
            // ALECE ("nada a descontar este mes"); quem le o relatorio precisa saber
            // que foi isso, e nao uma geracao que falhou no meio.
            avisos.push(`a competência ${entrada.competencia} está sem dívidas: o arquivo sai só com o cabeçalho, e nenhuma linha de desconto`);
        }
        if (!identificadorEscrito) {
            avisos.push(`${dividasSomadas.length} grupo(s) de dívidas saíram somados e sem a ADF: o retorno delas vai cair no ` +
                "casamento por matrícula, verba e valor");
        }
        return {
            arquivos: [
                {
                    nome: this.nomeArquivo(entrada),
                    texto: `${linhas.join("\r\n")}\r\n`,
                    codificacao: "latin1",
                    contentType: "text/plain; charset=iso-8859-1",
                },
            ],
            relatorio: {
                layout: { codigo: CODIGO, versao: versao() },
                competencia: entrada.competencia,
                linhas: linhas.length - 1,
                dividasInstruidas: entrada.descontos.length,
                dividasSomadas,
                identificadorEscrito,
                camposSemCorrespondencia,
                faltantesPorCampo,
                totaisPorCredor: resumirPorCredor(entrada.descontos),
                avisos,
            },
        };
    },
    /** `ALECE-MOVIMENTO-AAMM.TXT`, com `-PREVIA` enquanto o corte não passou. */
    nomeArquivo(entrada) {
        const aamm = `${entrada.competencia.slice(2, 4)}${entrada.competencia.slice(4, 6)}`;
        const previa = entrada.congelada === false ? "-PREVIA" : "";
        return `ALECE-MOVIMENTO-${aamm}${previa}.TXT`;
    },
};
//# sourceMappingURL=alece-movimento.js.map